<script lang="ts">
/**
 * Escapes a popover has used, so it closes only one popover: the innermost.
 * The trigger and the panel take the key on its way up (`onKeydown` on both),
 * before an ancestor that stops it — a toolbar owning its keys, a side panel
 * closing on Escape — and before the document listener, which serves a focus
 * outside the popover. A nested popover's panel is inside the outer one, so
 * the outer would see the same key next.
 */
const usedEscapes = new WeakSet<KeyboardEvent>();
</script>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import type { VirtualElement } from '@floating-ui/vue';
import LoadingStateBoundary from '../Inputs/LoadingStateBoundary.vue';
import { containOverlayWheel } from '../lib/containOverlayWheel';
import { useTableFocusClaim } from '../Table/focusScope';
import { useFloatingPanel } from './useFloatingPanel';

export type NxPopoverSide =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

/**
 * A box in viewport pixels the panel opens against — a cell of a canvas grid,
 * the caret of an editor. `width` / `height` default to 0 (a point).
 * `contextElement` is the DOM surface the box lies on (the canvas): floating-ui
 * follows its scrolling, and the teleport target is taken from it.
 */
export interface NxPopoverRectAnchor {
  x: number;
  y: number;
  width?: number;
  height?: number;
  contextElement?: HTMLElement;
}

export interface NxPopoverProps {
  /** Controlled open state (v-model:open). Omit for self-managed. */
  open?: boolean;
  /**
   * Opens the panel against this element or viewport box instead of a wrapped
   * trigger: no `#trigger`, no wrapper node, no click toggle — the caller owns
   * `open` (v-model:open) and the focus. For a surface that has no element to
   * wrap: a canvas grid's filter list under a header cell, a suggestion list
   * under an editor's caret. Outside presses close it, except on an element
   * anchor; Escape closes it and leaves the focus where it is.
   */
  anchor?: HTMLElement | NxPopoverRectAnchor | null;
  /**
   * Drops the panel's inner padding, for content that brings its own edge —
   * a list of options whose rows run to the border. Default false.
   */
  flush?: boolean;
  /**
   * The panel keeps to the height left on its side of the anchor and scrolls
   * inside it, for content that can grow past the screen (a filter list with
   * its condition fields). Default false.
   */
  fitHeight?: boolean;
  /**
   * The area the panel keeps inside instead of the viewport — a spreadsheet's
   * grid, so a header's filter list never covers the sheet tabs below it.
   */
  boundary?: HTMLElement | null;
  /**
   * Presses never move the focus: neither one on the panel nor the outside
   * press that closes it. For a panel that serves a field which keeps typing —
   * a suggestion list under a formula editor, whose blur would commit the
   * unfinished formula. Only for panels without fields of their own. Default
   * false.
   */
  keepFocus?: boolean;
  /** Preferred side (optionally with -start/-end alignment); floating-ui flips it when there is no room. Default 'bottom'. */
  side?: NxPopoverSide;
  /** Gap in px between trigger and panel. Default 8. */
  offset?: number;
  /** Min px kept from the viewport edge (shift). Default 8. */
  padding?: number;
  /** Render a beak pointing at the trigger. Default false. */
  arrow?: boolean;
  /**
   * Mount the panel only while open (v-if instead of v-show). Without it the
   * hidden panel stays mounted and floating-ui's autoUpdate keeps listening to
   * every ancestor scroll — fine for a single popover, but a scroll-perf killer
   * when many instances sit inside one scroll container (e.g. one per table
   * column header). Panel content state resets on close. Default false.
   */
  lazy?: boolean;
  /** Accessible name of the panel (`role="dialog"`). Optional; unnamed by default. */
  label?: string;
  /**
   * Escape typed into a field inside the panel closes it too, unless that
   * field used the key itself (`preventDefault`: close find, leave a menu,
   * IME). For a panel that is one editor — NxEditorInput's quick editor, where
   * closing commits. Default false: fields in the panel keep Escape, so a
   * nested list or an unfinished draft is never closed from under the user.
   */
  escapeFromFields?: boolean;
  /**
   * Render the panel through `<Teleport>`: to `<body>`, or into the closest
   * `<dialog>` / `[popover]` around the trigger when it sits in one. For a
   * panel that must leave a container which styles or intercepts its children
   * (a chip menu inside `NxTagField`). Anchoring, the manual popover, the
   * dialog role, outside-press close and Escape stay as they are. Default
   * false: the panel stays the anchor's next sibling, with no `<Teleport>`
   * node at all. A teleported panel needs a real `<Teleport>` in tests —
   * Vue Test Utils' `stubs: { teleport: true }` remounts the panel on every
   * render, which floating-ui cannot settle (hence: default stays Teleport-free).
   */
  teleport?: boolean;
}

const props = withDefaults(defineProps<NxPopoverProps>(), {
  open: undefined,
  anchor: undefined,
  flush: false,
  fitHeight: false,
  boundary: null,
  keepFocus: false,
  side: 'bottom',
  offset: 8,
  padding: 8,
  arrow: false,
  lazy: false,
  label: undefined,
  escapeFromFields: false,
  teleport: false,
});

const emit = defineEmits<{
  'update:open': [boolean];
  /**
   * The panel is shown. A browser hides a `popover` element until
   * `showPopover()`, which runs a tick after `open` flips — a host that focuses
   * into the panel on `open` would find it hidden and the focus would fail.
   */
  opened: [];
}>();

const panelId = useId();
/** The wrapper around `#trigger`; unused in anchored mode. */
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const anchored = computed(() => props.anchor !== undefined);

/** The anchor wins; without one the wrapped trigger is the reference. */
const floatingReference = computed<HTMLElement | VirtualElement | null>(() => {
  const anchor = props.anchor;
  if (anchor === undefined) return reference.value;
  if (anchor === null || anchor instanceof HTMLElement) return anchor;
  const { x, y, width = 0, height = 0, contextElement } = anchor;
  return {
    getBoundingClientRect: () => ({
      x,
      y,
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
      width,
      height,
    }),
    contextElement,
  };
});

/** The element the panel belongs to in the DOM: the trigger, or the anchor's. */
const ownerElement = computed<HTMLElement | null>(() => {
  const anchor = props.anchor;
  if (anchor === undefined) return reference.value;
  if (anchor === null || anchor instanceof HTMLElement) return anchor;
  return anchor.contextElement ?? null;
});

/** Presses here do not count as outside: the trigger, or an element anchor. */
const insideElement = computed<HTMLElement | null>(() =>
  anchored.value ? (props.anchor instanceof HTMLElement ? props.anchor : null) : reference.value
);

/**
 * Where a teleported panel goes. `<body>` by default. A modal `<dialog>` and
 * an open `[popover]` are the top layer, which paints above the document, so a
 * panel parked in `<body>` would open behind the very dialog it belongs to.
 * The anchor's closest one wins, and the panel stays in that top layer.
 */
const teleportTarget = computed<HTMLElement | 'body'>(
  // `closest` is typed `Element | null` without the parameter, and `Element` is
  // not assignable to the declared `HTMLElement` — every app that compiles this
  // package from source then fails to build, while `frontend/erp` stays green
  // because it resolves the package through its built types.
  () => ownerElement.value?.closest<HTMLElement>('dialog, [popover]') ?? 'body'
);
const arrowEl = ref<HTMLElement | null>(null);

const isOpen = ref(props.open ?? false);
// Opened from a table, it belongs to that table's editing wherever it renders.
useTableFocusClaim(floating, () => isOpen.value);
watch(
  () => props.open,
  (v) => {
    if (v !== undefined) isOpen.value = v;
  }
);

/**
 * Lazy panels unmount only after `isOpen` is still false on the next tick.
 * A parent can revert `v-model:open` in the same turn (commit-then-close)
 * without destroying slotted editors mid-`prepareSave`.
 */
const lazyMounted = ref(!props.lazy || isOpen.value);
watch(isOpen, (open) => {
  if (!props.lazy) {
    lazyMounted.value = true;
    return;
  }
  if (open) {
    lazyMounted.value = true;
    return;
  }
  void nextTick(() => {
    if (!isOpen.value) lazyMounted.value = false;
  });
});

const { floatingStyles, placement, middlewareData, update } = useFloatingPanel(
  floatingReference,
  floating,
  {
    open: isOpen,
    placement: () => props.side,
    offset: () => props.offset,
    padding: () => props.padding,
    fitHeight: () => props.fitHeight,
    boundary: () => props.boundary,
    // Always wire the arrow element. floating-ui's arrow middleware no-ops when
    // the element is absent (arrow=false → the arrow <div> is not rendered).
    arrowEl,
  }
);

/**
 * One attribute set for both panel branches. Without `teleport` the panel is
 * the plain div; with it, that same div sits in `<Teleport>`. A second copy
 * of this list would drift.
 */
const panelAttrs = computed(() => ({
  id: panelId,
  class: ['nx-popover-panel', { 'is-flush': props.flush, 'is-fit-height': props.fitHeight }],
  popover: 'manual' as const,
  role: 'dialog' as const,
  'aria-label': props.label,
  style: floatingStyles.value,
}));

// Reposition when the arrow toggles at runtime so the beak is measured once its
// element exists.
watch(
  () => props.arrow,
  () => {
    void nextTick(() => update());
  }
);

/**
 * Show the open panel again after its teleport target changes, and after
 * `teleport` itself flips either way while the panel is open.
 * On the first render the anchor ref is still null, so the target is `body`
 * and `showPopover()` runs there. The ref then resolves and Vue moves the
 * panel into the dialog or popover that holds the anchor. Moving a shown
 * popover hides it, while `isOpen` stays true. Toggling `teleport` remounts
 * the panel — the plain div and the one inside `<Teleport>` are different
 * elements — and the new element was never `showPopover()`ed. `flush: 'post'`
 * runs once that move is in the DOM, so the panel is shown where it landed
 * and floating-ui measures the element that is actually on screen.
 */
watch(
  [teleportTarget, () => props.teleport],
  ([, teleport], previous) => {
    if (!isOpen.value) return;
    // A target change while the panel stays in place has nothing to re-show.
    // Turning `teleport` off does: the replacement element is a new popover.
    const teleportChanged = previous !== undefined && previous[1] !== teleport;
    if (!teleport && !teleportChanged) return;
    const el = floating.value;
    if (el && 'showPopover' in el) {
      try {
        el.showPopover();
      } catch {
        /* already open */
      }
    }
    void update();
  },
  { flush: 'post' }
);

const arrowStyle = computed(() => {
  const data = middlewareData.value.arrow;
  if (!data) return {};
  const side = placement.value.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';
  const staticSide = ({ top: 'bottom', bottom: 'top', left: 'right', right: 'left' } as const)[
    side
  ];
  // Which two edges of the 45°-rotated square face outward (toward the trigger)
  // depends on which panel edge the arrow sits on (its static side).
  const outwardBorders: Record<'top' | 'bottom' | 'left' | 'right', [string, string]> = {
    top: ['borderTop', 'borderLeft'],
    bottom: ['borderBottom', 'borderRight'],
    left: ['borderLeft', 'borderBottom'],
    right: ['borderTop', 'borderRight'],
  };
  const [borderA, borderB] = outwardBorders[staticSide];
  return {
    left: data.x != null ? `${data.x}px` : '',
    top: data.y != null ? `${data.y}px` : '',
    [staticSide]: '-4px',
    [borderA]: '1px solid var(--nxt-popover-border)',
    [borderB]: '1px solid var(--nxt-popover-border)',
  } as Record<string, string>;
});

function setOpen(next: boolean) {
  // Controlled: only the v-model may flip `isOpen`. An optimistic local close
  // would unmount a lazy panel before the parent can revert `open` —
  // true→false→true is a no-op for the prop watcher, so `isOpen` would stay
  // false and destroy slotted editors mid-commit.
  if (props.open === undefined) {
    isOpen.value = next;
  }
  emit('update:open', next);
}
function toggle() {
  setOpen(!isOpen.value);
}
function close() {
  if (isOpen.value) setOpen(false);
}

function onDocPointerDown(e: PointerEvent) {
  const target = e.target as Node;
  if (insideElement.value?.contains(target) || floating.value?.contains(target)) return;
  // A press into the focused field itself moves no focus; it places the caret.
  if (props.keepFocus && !document.activeElement?.contains(target)) holdFocusThroughPress();
  close();
}

/** The focus moves on `mousedown`, which follows this `pointerdown`. */
function preventFocusMove(e: MouseEvent) {
  e.preventDefault();
}

/**
 * The closing press is cancelled on its `mousedown`, not here: a controlled
 * owner closes in this `pointerdown` and the panel's listeners are gone by the
 * time the `mousedown` arrives. This one stays until that `mousedown` — or the
 * next press, when a touch scroll never sent one.
 */
let releaseHeldPress: (() => void) | null = null;
function holdFocusThroughPress() {
  releaseHeldPress?.();
  const release = () => {
    document.removeEventListener('mousedown', onHeldMousedown, true);
    document.removeEventListener('pointerdown', release, true);
    if (releaseHeldPress === release) releaseHeldPress = null;
  };
  const onHeldMousedown = (e: MouseEvent) => {
    preventFocusMove(e);
    release();
  };
  document.addEventListener('mousedown', onHeldMousedown, true);
  // Added during this press's dispatch, so it first runs on the next press.
  document.addEventListener('pointerdown', release, true);
  releaseHeldPress = release;
}

/** IME keydown uses keyCode 229 on some engines even when `isComposing` is late. */
function isImeKey(e: KeyboardEvent): boolean {
  return e.isComposing || e.keyCode === 229;
}

/**
 * Write's hidden IME textarea, find/replace fields, and contenteditable
 * surfaces own Escape (cancel composition / close find). Capture would
 * close us before those handlers run; on bubble we still yield when the
 * target is one of those fields so a missing `stopPropagation` cannot
 * commit an unfinished draft.
 */
function isInnerEditorTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const field = target.closest('input, textarea, [contenteditable]:not([contenteditable="false"])');
  // A radio or checkbox has no draft and no use for Escape: it closes the panel.
  return !!field && !(field instanceof HTMLInputElement && CHOICE_INPUT_TYPES.has(field.type));
}

/** Inputs that are a choice, not typed text — Escape there is the panel's. */
const CHOICE_INPUT_TYPES = new Set(['radio', 'checkbox']);

function onPanelWheel(event: WheelEvent) {
  // Template refs unwrap in the template; here the ref object is not an element.
  containOverlayWheel(event, floating.value);
}

function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !isOpen.value) return;
  if (isImeKey(e) || usedEscapes.has(e)) return;
  const target = e.target;
  if (target instanceof Node && floating.value?.contains(target) && isInnerEditorTarget(target)) {
    // escapeFromFields: only a key the field consumed stays with it.
    if (!props.escapeFromFields || e.defaultPrevented) return;
  }
  // Consumed: a host's own Escape handler further up (a document editor
  // closing its find bar, a side panel) sees `defaultPrevented` and leaves it
  // alone; another popover's listener sees it in `usedEscapes`.
  usedEscapes.add(e);
  e.preventDefault();
  close();
  if (!anchored.value) focusTrigger();
}

/**
 * Back to the trigger after Escape: its first real Tab stop, not an action
 * icon that forms keep out of the Tab order (`tabindex="-1"`).
 */
function focusTrigger() {
  const root = reference.value;
  const tabStop = 'button, a[href], [tabindex]';
  const reachable = tabStop
    .split(', ')
    .map((sel) => `${sel}:not([tabindex="-1"])`)
    .join(', ');
  (
    root?.querySelector<HTMLElement>(reachable) ?? root?.querySelector<HTMLElement>(tabStop)
  )?.focus();
}

/** Same listeners on the in-place panel and the teleported one. */
const panelEvents = {
  keydown: onKeydown,
  wheel: onPanelWheel,
  mousedown: (e: MouseEvent) => {
    if (props.keepFocus) preventFocusMove(e);
  },
};

watch(
  isOpen,
  (open) => {
    nextTick(() => {
      if (isOpen.value !== open) return;
      const el = floating.value;
      if (open) {
        if (el && 'showPopover' in el) {
          try {
            el.showPopover();
          } catch {
            /* already open */
          }
        }
        update();
        document.addEventListener('pointerdown', onDocPointerDown, true);
        // Bubble: inner editors can stopPropagation (find / IME). Outside-click
        // stays capture so a press on a covered page control still dismisses.
        document.addEventListener('keydown', onKeydown);
        emit('opened');
      } else {
        if (el && 'hidePopover' in el) {
          try {
            el.hidePopover();
          } catch {
            /* already closed */
          }
        }
        document.removeEventListener('pointerdown', onDocPointerDown, true);
        document.removeEventListener('keydown', onKeydown);
      }
    });
  },
  { immediate: true }
);

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div
    v-if="!anchored"
    ref="reference"
    class="nx-popover-anchor"
    aria-haspopup="dialog"
    :aria-expanded="isOpen"
    :aria-controls="panelId"
    @click="toggle"
    @keydown="onKeydown"
  >
    <slot name="trigger" />
  </div>

  <Teleport v-if="props.teleport" :to="teleportTarget">
    <div
      v-if="!props.lazy || lazyMounted"
      v-show="isOpen"
      ref="floating"
      v-bind="panelAttrs"
      v-on="panelEvents"
    >
      <LoadingStateBoundary><slot /></LoadingStateBoundary>
      <div v-if="arrow" ref="arrowEl" class="nx-popover-arrow" :style="arrowStyle" />
    </div>
  </Teleport>
  <div
    v-else-if="!props.lazy || lazyMounted"
    v-show="isOpen"
    ref="floating"
    v-bind="panelAttrs"
    v-on="panelEvents"
  >
    <LoadingStateBoundary><slot /></LoadingStateBoundary>
    <div v-if="arrow" ref="arrowEl" class="nx-popover-arrow" :style="arrowStyle" />
  </div>
</template>

<style scoped>
.nx-popover-anchor {
  display: inline-block;
}

.nx-popover-panel {
  position: fixed;
  width: max-content;
  max-width: min(90vw, var(--nxt-panel-width-2xl));
  margin: 0;
  padding: var(--nxt-space-md);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-popover-bg);
  color: var(--nxt-popover-fg);
  box-shadow: var(--nxt-popover-shadow);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  overflow: visible;
  overscroll-behavior: contain;
}
.nx-popover-panel.is-flush {
  padding: 0;
}
/* The room beside the anchor (useFloatingPanel `fitHeight`); the panel scrolls
   as a whole rather than squeezing what it holds. */
.nx-popover-panel.is-fit-height {
  box-sizing: border-box;
  max-height: var(--floating-available-height, calc(100vh - var(--nxt-space-md)));
  overflow-y: auto;
}
/* The global type reset sizes bare <p>/<ul> at 16px medium; the panel sets it. */
.nx-popover-panel :deep(:is(p, ul, ol)) {
  font-size: inherit;
  font-weight: inherit;
}

.nx-popover-arrow {
  position: absolute;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  background: var(--nxt-popover-bg);
  transform: rotate(45deg);
}
</style>
